import { ACL_TYPE } from '@openx/types/aclType';
import { CuratedPackageAction } from '@openx/types/package';
import type { HasCuratedPermission, UsePackagePermissions } from '@openx/types/package/PackageHookTypes';
import { UserRole } from '@openx/types/user';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl, isUserAllowedByAcl } from '@openx/utils/permission/validators';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../components/AuthContext/context';
import { isInternalAdmin } from '../../../permissions/validators';

export const usePackagePermissions: UsePackagePermissions = () => {
  const { useIsAllowed } = usePermissionContext();
  const { session } = useAuth();
  const { accessUser, isAllowed } = useIsAllowed();

  const shouldSeeOnBehalfOf = isInternalAdmin(session);
  const isCurator = useMemo(() => isAllowed(isUserAllowedByAcl(ACL_TYPE.FEATURE_PACKAGE_CURATOR)), [isAllowed]);

  const isOaInternal = useMemo(() => !!accessUser?.roles.includes(UserRole.NETWORK_OA_INTERNAL), [accessUser?.roles]);

  const isInternalUser = useMemo(
    () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) || isOaInternal,
    [isAllowed, isOaInternal]
  );

  const hasCuratedPermission: HasCuratedPermission = useCallback(
    ({ pckg, action }) => {
      if (!pckg) {
        return false;
      }

      if (!pckg.is_curated) {
        return true;
      }

      switch (action) {
        case CuratedPackageAction.UPDATE:
        case CuratedPackageAction.DELETE:
          return isCurator;
        case CuratedPackageAction.SHOW_LIST_TAB:
          return true;
      }
    },
    [isCurator]
  );

  return {
    hasCuratedPermission,
    isInternalUser,
    shouldSeeOnBehalfOf,
  };
};
